import { useAuthContext } from "@/hooks/useAuthContext";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Archive, FileText } from "lucide-react";
import { useRealtimeTranscriptions } from "@/hooks/useRealtimeTranscriptions";
import { type TranscriptionData } from "@/redux/api/transcriptions.rtkq";
import TranscriptionRow from "./TranscriptionRow";
import TranscriptionViewer from "./TranscriptionViewer";
import PendingTranscriptionRow from "./PendingTranscriptionRow";
import { hasActiveTranscription, useStaleTick } from "./transcriptionStale";
import { cn } from "@/lib/utils";
import { useRecordingState } from "@/contexts/RecordingStateContext";

interface RecordingsTableProps {
  pendingTranscription?: {
    fileName: string;
    currentStep: "uploading" | "queued" | "processing" | "completed" | "error";
    progress: number;
    error?: string;
    id?: string;
    createdAt?: string;
    storagePath?: string;
  } | null;
  onPendingTranscriptionCleared?: () => void;
  isReadOnly?: boolean;
}

type SortField = "title" | "createdAt" | "duration";
type SortDirection = "asc" | "desc";

const getTranscriptionTitle = (transcription: TranscriptionData): string =>
  (transcription.summary?.title || transcription.fileName || "").toLowerCase();

const compareDuration = (a: number, b: number, direction: SortDirection): number => {
  const aFinite = Number.isFinite(a);
  const bFinite = Number.isFinite(b);
  if (!aFinite && !bFinite) return 0;
  if (!aFinite) return 1;
  if (!bFinite) return -1;
  if (a < b) return direction === "asc" ? -1 : 1;
  if (a > b) return direction === "asc" ? 1 : -1;
  return 0;
};

const RecordingsTable: React.FC<RecordingsTableProps> = ({ pendingTranscription, onPendingTranscriptionCleared, isReadOnly = false }) => {
  const { user } = useAuthContext();
  const { isArchiveMode: showDeleted, setIsArchiveMode: setShowDeleted } = useRecordingState();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTranscription, setSelectedTranscription] = useState<TranscriptionData | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleViewTranscription = (transcription: TranscriptionData) => {
    setSelectedTranscription(transcription);
    setIsViewerOpen(true);
  };

  const {
    transcriptions: transcriptionsData,
    isLoading,
    error,
  } = useRealtimeTranscriptions({
    userId: user?.uid || "",
    enabled: !!user?.uid,
  });

  // Stuck rows produce no document update, so nothing would re-render them.
  // This tick lets TranscriptionRow re-evaluate staleness while work is pending.
  useStaleTick(hasActiveTranscription(transcriptionsData || []));

  const matchesPendingTranscription = useCallback(
    (transcription: TranscriptionData) => {
      if (!pendingTranscription) return false;
      if (pendingTranscription.id && transcription.id === pendingTranscription.id) {
        return true;
      }

      return (
        transcription.fileName === pendingTranscription.fileName &&
        transcription.createdAt === pendingTranscription.createdAt &&
        transcription.storagePath === pendingTranscription.storagePath
      );
    },
    [pendingTranscription]
  );

  // Failed uploads already live in Firestore — keep that row so delete/resend stay available.
  const pendingErrorHasTableRow =
    pendingTranscription?.currentStep === "error" && !!transcriptionsData?.some(matchesPendingTranscription);

  const pendingMatchesDeletedRow =
    !!pendingTranscription && !!transcriptionsData?.some((transcription) => transcription.isDeleted && matchesPendingTranscription(transcription));

  // Filter out pending transcriptions from the main list to prevent duplicates
  const filteredTranscriptionsData = useMemo(() => {
    if (!pendingTranscription || !transcriptionsData) return transcriptionsData;
    if (pendingTranscription.currentStep === "error") return transcriptionsData;

    return transcriptionsData.filter((transcription) => !matchesPendingTranscription(transcription));
  }, [transcriptionsData, pendingTranscription, matchesPendingTranscription]);

  // Check if pending transcription has been completed
  const isPendingTranscriptionCompleted = pendingTranscription?.currentStep === "completed";

  // Check if the completed transcription actually appears in the filtered data
  const hasCompletedTranscriptionAppeared =
    isPendingTranscriptionCompleted &&
    filteredTranscriptionsData?.some((t) => {
      // Use ID if available for precise matching
      if (pendingTranscription?.id && t.id === pendingTranscription.id) {
        return true;
      }

      // Fallback to filename + createdAt + storagePath matching
      return (
        t.fileName === pendingTranscription?.fileName &&
        t.createdAt === pendingTranscription?.createdAt &&
        t.storagePath === pendingTranscription?.storagePath &&
        t.processing.status === "completed"
      );
    });

  // Update selectedTranscription when transcriptions data changes
  useEffect(() => {
    if (selectedTranscription && filteredTranscriptionsData) {
      const updatedTranscription = filteredTranscriptionsData.find((t) => t.id === selectedTranscription.id);
      if (updatedTranscription) {
        setSelectedTranscription(updatedTranscription);
      }
    }
  }, [filteredTranscriptionsData, selectedTranscription]);

  // Clear pending transcription when it's completed and appears in the table
  useEffect(() => {
    if (hasCompletedTranscriptionAppeared && onPendingTranscriptionCleared) {
      // The completed transcription has appeared in the filtered list, clear pending
      onPendingTranscriptionCleared();
    }
  }, [hasCompletedTranscriptionAppeared, onPendingTranscriptionCleared]);

  useEffect(() => {
    if (pendingErrorHasTableRow && onPendingTranscriptionCleared) {
      onPendingTranscriptionCleared();
    }
  }, [pendingErrorHasTableRow, onPendingTranscriptionCleared]);

  useEffect(() => {
    if (pendingMatchesDeletedRow && onPendingTranscriptionCleared) {
      onPendingTranscriptionCleared();
    }
  }, [pendingMatchesDeletedRow, onPendingTranscriptionCleared]);

  // Handle cold start delays - keep checking for completed transcription to appear
  useEffect(() => {
    if (isPendingTranscriptionCompleted && !hasCompletedTranscriptionAppeared && onPendingTranscriptionCleared) {
      // Transcription is completed but hasn't appeared yet (cold start delay)
      const checkForAppearance = () => {
        const newTranscription = filteredTranscriptionsData?.find((t) => {
          // Use ID if available for precise matching
          if (pendingTranscription?.id && t.id === pendingTranscription.id) {
            return true;
          }

          // Fallback to filename + createdAt + storagePath matching
          return (
            t.fileName === pendingTranscription?.fileName &&
            t.createdAt === pendingTranscription?.createdAt &&
            t.storagePath === pendingTranscription?.storagePath &&
            t.processing.status === "completed"
          );
        });

        if (newTranscription) {
          // New row has appeared, clear the pending transcription
          onPendingTranscriptionCleared();
        } else {
          // Check again in 500ms
          setTimeout(checkForAppearance, 500);
        }
      };

      // Start checking after a brief delay to allow user to see completion
      const initialTimer = setTimeout(checkForAppearance, 1000);

      // Fallback timeout to prevent infinite waiting (max 15 seconds for cold starts)
      const fallbackTimer = setTimeout(() => {
        console.warn("Pending transcription cleanup timeout (cold start) - forcing cleanup");
        onPendingTranscriptionCleared();
      }, 3000);

      return () => {
        clearTimeout(initialTimer);
        clearTimeout(fallbackTimer);
      };
    }
  }, [
    isPendingTranscriptionCompleted,
    hasCompletedTranscriptionAppeared,
    onPendingTranscriptionCleared,
    filteredTranscriptionsData,
    pendingTranscription,
  ]);

  // Filter and sort transcriptions based on search, deleted status, and header sort
  const filteredTranscriptions = useMemo(() => {
    const filtered =
      filteredTranscriptionsData?.filter((transcription) => {
        const matchesSearch =
          transcription.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transcription.transcription.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesDeletedFilter = showDeleted ? transcription.isDeleted : !transcription.isDeleted;

        return matchesSearch && matchesDeletedFilter;
      }) || [];

    return [...filtered].sort((a, b) => {
      if (sortField === "title") {
        const comparison = getTranscriptionTitle(a).localeCompare(getTranscriptionTitle(b), "he");
        return sortDirection === "asc" ? comparison : -comparison;
      }

      if (sortField === "duration") {
        return compareDuration(a.metadata.duration, b.metadata.duration, sortDirection);
      }

      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (aTime < bTime) return sortDirection === "asc" ? -1 : 1;
      if (aTime > bTime) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredTranscriptionsData, searchTerm, showDeleted, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      return;
    }

    setSortField(field);
    setSortDirection(field === "title" ? "asc" : "desc");
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="mr-2 h-4 w-4 opacity-50" />;
    return sortDirection === "asc" ? <ArrowUp className="mr-2 h-4 w-4" /> : <ArrowDown className="mr-2 h-4 w-4" />;
  };

  // Separate active and deleted transcriptions for display (for statistics)
  const activeTranscriptions = filteredTranscriptionsData?.filter((t) => !t.isDeleted) || [];
  const deletedTranscriptions = filteredTranscriptionsData?.filter((t) => t.isDeleted) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">טוען...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-destructive">שגיאה: {error}</div>
      </div>
    );
  }

  if (!transcriptionsData) {
    return null;
  }

  return (
    <div
      className={cn(
        "container flex h-full flex-col rounded-xl border border-transparent px-4 py-4 transition-colors md:px-10",
        showDeleted && "border-amber-200 bg-amber-50/50"
      )}
    >
      {/* Header with search and controls */}
      <div className="container mb-4 flex flex-col gap-4">
        <div className="flex flex-col gap-3 md:flex-row-reverse md:items-center md:justify-between">
          <div className="relative w-full md:w-80">
            <Search className={`absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 transform ${isReadOnly ? 'text-muted-foreground/50' : 'text-muted-foreground'}`} />
            <Input
              placeholder={isReadOnly ? "חיפוש (לא זמין)" : showDeleted ? "חיפוש בסל המחזור" : "חיפוש"}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pr-10 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
              disabled={isReadOnly}
            />
          </div>
          <h2 className="text-xl font-semibold text-chen-900 md:text-2xl">
            {showDeleted ? "סל מחזור" : "תמלולים וסיכומים"}
            <span className="mr-2 text-base font-normal text-muted-foreground md:text-lg">
              {showDeleted
                ? `(${deletedTranscriptions.length} תמלולים)`
                : `(${activeTranscriptions.length} פעילים${deletedTranscriptions.length > 0 ? `, ${deletedTranscriptions.length} בסל המחזור` : ""})`}
            </span>
          </h2>
        </div>

        <Tabs
          value={showDeleted ? "deleted" : "active"}
          onValueChange={(value) => setShowDeleted(value === "deleted")}
          dir="rtl"
        >
          <TabsList
            aria-label="בחירת תצוגת תמלולים"
            className="h-11 w-full max-w-md rounded-xl border bg-muted/70 p-1 sm:w-auto"
          >
            <TabsTrigger value="active" className="gap-2 rounded-lg px-4 data-[state=active]:text-primary">
              <FileText className="size-4" aria-hidden />
              תמלולים פעילים
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums">
                {activeTranscriptions.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="deleted"
              disabled={isReadOnly}
              className="gap-2 rounded-lg px-4 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-900"
            >
              <Archive className="size-4" aria-hidden />
              סל המחזור
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 tabular-nums">
                {deletedTranscriptions.length}
              </span>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {showDeleted && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-100/80 px-4 py-3 text-amber-950" role="status">
            <Archive className="mt-0.5 size-5 shrink-0" aria-hidden />
            <div>
              <p className="font-semibold">מצב סל המחזור</p>
              <p className="text-sm text-amber-900/80">
                כאן ניתן לשחזר תמלולים או למחוק אותם לצמיתות. שחזור פריט לא יוציא אותך מתצוגה זו.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className={cn("flex-1 overflow-x-auto overflow-y-auto rounded-lg", showDeleted && "border border-amber-200 bg-background/80")}>
        <Table>
          <TableHeader className="sticky top-0">
            <TableRow>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold text-chen-900 hover:bg-transparent"
                  onClick={() => handleSort("title")}
                  aria-label="מיין לפי כותרת"
                >
                  כותרת
                  {getSortIcon("title")}
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold text-chen-900 hover:bg-transparent"
                  onClick={() => handleSort("createdAt")}
                  aria-label="מיין לפי תאריך ושעה"
                >
                  תאריך ושעה
                  {getSortIcon("createdAt")}
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold text-chen-900 hover:bg-transparent"
                  onClick={() => handleSort("duration")}
                  aria-label="מיין לפי זמן"
                >
                  זמן
                  {getSortIcon("duration")}
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <div className="font-semibold text-chen-900">סטטוס</div>
              </TableHead>
              <TableHead className="text-right">
                <div className="font-semibold text-chen-900">פעולות</div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!showDeleted && pendingTranscription && !pendingErrorHasTableRow && !pendingMatchesDeletedRow && (
              <PendingTranscriptionRow
                key="pending-transcription"
                fileName={pendingTranscription.fileName}
                currentStep={pendingTranscription.currentStep}
                progress={pendingTranscription.progress}
                error={pendingTranscription.error}
                id={pendingTranscription.id}
                createdAt={pendingTranscription.createdAt}
                storagePath={pendingTranscription.storagePath}
                isWaitingForSync={isPendingTranscriptionCompleted && !hasCompletedTranscriptionAppeared}
                isReadOnly={isReadOnly}
                onDismiss={onPendingTranscriptionCleared}
              />
            )}

            {filteredTranscriptions.map((transcription) => (
              <TranscriptionRow
                key={transcription.id}
                transcription={transcription}
                onViewTranscription={handleViewTranscription}
                isReadOnly={isReadOnly}
              />
            ))}

            {filteredTranscriptions.length === 0 &&
              (showDeleted || !pendingTranscription || pendingErrorHasTableRow || pendingMatchesDeletedRow) && (
                <TableRow key="empty-recordings">
                  <TableCell colSpan={5} className="h-24 text-center">
                    {showDeleted ? "אין תמלולים שנמחקו" : "אין תמלולים זמינים"}
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>

        {/* Transcription Viewer Modal */}
        {selectedTranscription && (
          <TranscriptionViewer transcription={selectedTranscription} isOpen={isViewerOpen} onOpenChange={setIsViewerOpen} />
        )}
      </div>
    </div>
  );
};

export default RecordingsTable;
